<script lang="ts" setup>
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import type { RunHistory } from '@/types'
import type { RunValidation } from '@/queries/saved-calls'
import ValidationDiffTable from '@/views/testing/components/ValidationDiffTable.vue'
import {
  formatDuration,
  formatRunTimeAbsolute,
  getRunStatusBadgeClass,
  getRunStatusLabel,
  getValidationBadgeClass,
  getValidationLabel,
} from '../utils/runFormatters'

const props = defineProps<{
  run: RunHistory | null
  /** Project id, used to build the "Open in Builder" link. Optional — when omitted the
   *  link is hidden (e.g. when the header is mounted inside the builder itself). */
  projectId?: string
  /** Hide the "Open in Builder" button (e.g. when already in the builder). */
  hideOpenInBuilder?: boolean
  /** Validation outcome for this run against its baseline, if one was recorded. */
  validation?: RunValidation | null
}>()

const copied = ref(false)

// The per-service expected-vs-actual diff is collapsed behind the validation badge.
const showDiffs = ref(false)
const hasDiffs = computed(() => (props.validation?.serviceDiffs.length ?? 0) > 0)

// Per-run inputs the run was invoked with (snapshotted at completion). Read-only here — this is
// for inspecting what a past run was fed; re-run editing lives in the Run modal.
const showInputs = ref(false)
const inputEntries = computed(() =>
  Object.entries(props.run?.inputs ?? {}).map(([key, value]) => ({ key, value: formatInputValue(value) }))
)

// JSON literal for every value: strings keep quotes, null/booleans read unambiguously, and
// objects/arrays render as compact JSON. Falls back to String() for the rare non-serialisable value.
function formatInputValue(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const builderLink = computed(() => {
  if (!props.run || !props.projectId) return ''
  return `/projects/${props.projectId}/builder/${props.run.workflowId}/history/${props.run.id}`
})

// All run-level errors — the first is always shown; the rest collapse behind a "+N more" toggle.
const errors = computed(() => props.run?.errors ?? [])
const showAllErrors = ref(false)
const visibleErrors = computed(() =>
  showAllErrors.value ? errors.value : errors.value.slice(0, 1)
)
const hiddenErrorCount = computed(() => Math.max(0, errors.value.length - 1))

async function copyRunId() {
  if (!props.run?.id) return
  try {
    await navigator.clipboard.writeText(props.run.id)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 1500)
  } catch {
    // clipboard unavailable; ignore
  }
}
</script>

<template>
  <div v-if="run" class="run-summary-header">
    <div class="d-flex flex-wrap align-items-center gap-3 p-4 summary-row">
      <span class="badge summary-badge" :class="getRunStatusBadgeClass(run.status)">
        <span
          v-if="run.status === 'Running' || run.status === 'Pending'"
          class="spinner-border spinner-border-sm me-1"
          style="width: 0.7rem; height: 0.7rem"
          role="status"
        ></span>
        {{ getRunStatusLabel(run.status) }}
      </span>

      <component
        :is="hasDiffs ? 'button' : 'span'"
        v-if="validation"
        :type="hasDiffs ? 'button' : undefined"
        class="badge summary-badge"
        :class="[getValidationBadgeClass(validation.outcome), { 'cursor-pointer border-0': hasDiffs }]"
        :title="hasDiffs ? 'Show per-service comparison against the baseline' : `Run validated against baseline: ${validation.outcome}`"
        @click="hasDiffs && (showDiffs = !showDiffs)"
      >
        <i class="bi bi-clipboard-check me-1"></i>{{ getValidationLabel(validation.outcome) }}
        <i v-if="hasDiffs" class="bi ms-1" :class="showDiffs ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </component>

      <div class="d-flex flex-column summary-main">
        <div class="d-flex align-items-center gap-2 summary-title-row">
          <h3 class="mb-0 summary-title">{{ run.workflowName }}</h3>
          <button
            type="button"
            class="btn btn-sm btn-link p-0 text-muted run-id-btn"
            :title="copied ? 'Copied!' : 'Copy run ID'"
            @click="copyRunId"
          >
            <small class="font-monospace">
              <span class="d-none d-md-inline me-1">{{ run.id }}</span>
              <i class="bi" :class="copied ? 'bi-check' : 'bi-clipboard'"></i>
            </small>
          </button>
        </div>
        <small class="text-muted">
          <i class="bi bi-calendar3 me-1"></i>
          {{ formatRunTimeAbsolute(run.startedAt) }}
          <span class="mx-2">&middot;</span>
          <i class="bi bi-clock me-1"></i>
          {{ formatDuration(run.durationInMilliSeconds) }}
        </small>
        <button
          v-if="inputEntries.length"
          type="button"
          class="btn btn-sm btn-link p-0 text-muted align-self-start inputs-toggle"
          :aria-expanded="showInputs"
          @click="showInputs = !showInputs"
        >
          <small>
            <i class="bi me-1" :class="showInputs ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            Inputs ({{ inputEntries.length }})
          </small>
        </button>
      </div>

      <div class="ms-auto d-flex align-items-center gap-2">
        <RouterLink
          v-if="!hideOpenInBuilder && builderLink"
          :to="builderLink"
          class="bb-btn-secondary-cta btn-sm"
        >
          <i class="bi bi-wrench me-1"></i>
          Open in Builder
        </RouterLink>
        <slot name="actions" />
      </div>
    </div>

    <div v-if="showInputs && inputEntries.length" class="border-top inputs-panel">
      <dl class="row mb-0 px-4 py-3 small">
        <template v-for="entry in inputEntries" :key="entry.key">
          <dt class="col-4 col-md-3 text-truncate fw-semibold" :title="entry.key">{{ entry.key }}</dt>
          <dd class="col-8 col-md-9 mb-1 font-monospace text-break">{{ entry.value }}</dd>
        </template>
      </dl>
    </div>

    <div v-if="showDiffs && validation" class="border-top p-3">
      <ValidationDiffTable mode="diff" :diffs="validation.serviceDiffs" />
    </div>

    <div v-if="errors.length" class="rounded-bottom overflow-hidden border-top">
      <div
        v-for="(err, i) in visibleErrors"
        :key="i"
        class="alert alert-danger mb-0 rounded-0 py-2 small border-0"
        :class="{ 'border-top': i > 0 }"
      >
        <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ err.message }}
      </div>
      <button
        v-if="hiddenErrorCount > 0"
        type="button"
        class="btn btn-sm btn-link text-danger w-100 text-start px-3 py-1"
        @click="showAllErrors = !showAllErrors"
      >
        {{ showAllErrors ? 'Show fewer' : `+${hiddenErrorCount} more error${hiddenErrorCount > 1 ? 's' : ''}` }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.run-summary-header {
  margin: 1.5rem 1rem 0.5rem 1rem;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-primary);
  border-radius: var(--bs-border-radius);
  background: var(--bs-body-bg);
}

// Mobile only — desktop layout is intentionally left untouched. We tighten
// spacing, shrink the title, keep the workflow name beside the status badge,
// and collapse the run-id text to its copy icon (hidden via d-md-inline).
@media (max-width: 768px) {
  .run-summary-header {
    margin: 0.75rem 0.5rem 0.5rem 0.5rem;
  }

  .summary-row {
    padding: 0.75rem !important; // override p-4
    gap: 0.5rem !important; // override gap-3
  }

  .summary-badge {
    flex-shrink: 0;
  }

  // Let the title block take the remaining width and shrink below its
  // content so the workflow name can truncate instead of wrapping.
  .summary-main {
    flex: 1 1 0;
    min-width: 0;
  }

  .summary-title-row {
    min-width: 0;
  }

  .summary-title {
    font-size: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
}

.run-id-btn {
  text-decoration: none;
}

.run-id-btn:hover small {
  text-decoration: underline;
}

.inputs-toggle {
  text-decoration: none;
}

.inputs-toggle:hover small {
  text-decoration: underline;
}
</style>
